import { API_BASE, refreshSession } from './api';
import { useAuthStore } from './auth-store';

/**
 * Fetch-based SSE client with Last-Event-ID resume.
 *
 * `EventSource` is not usable here: it cannot send an Authorization header, and
 * every endpoint on this API is bearer-authenticated. So we read the response
 * body ourselves and parse the wire format by hand.
 *
 * What this adds over a naive `data:`-line reader:
 *
 *  * **A real frame parser.** `id:`, `event:`, multi-line `data:`, `retry:` and
 *    `: comment` keep-alives are all part of the format. The stream goes quiet
 *    for long stretches while a model thinks, so the keep-alives are not
 *    optional decoration - dropping the parser means treating them as data.
 *  * **Resume.** Every frame's `id:` is an opaque cursor. We store the last one
 *    we actually processed and send it back as `Last-Event-ID` on reconnect;
 *    the server replays everything after it and then continues live. The query
 *    parameter is sent too, as the contract's documented fallback.
 *  * **No duplicates.** Resume is meant to be exact, but "meant to be" is not a
 *    guarantee we get to make about someone else's process. A bounded ring of
 *    recently-seen cursors drops a repeat if the server ever replays
 *    inclusively, so an at-least-once server still produces exactly-once UI.
 *  * **Reconnect with backoff.** A clean close mid-run is normal (the request
 *    context is cancelled on shutdown), so a disconnect is a reconnect, not an
 *    error to show a client.
 *  * **Token renewal.** A run outlives an access token. A 401 refreshes once
 *    and reconnects from the stored cursor rather than surfacing a failure.
 */

export type StreamPhase = 'connecting' | 'open' | 'reconnecting' | 'closed' | 'failed';

export interface StreamFrame {
  /** The `id:` field - the resume cursor. */
  id: string | null;
  /** The `event:` field. Defaults to `message` per the SSE spec. */
  event: string;
  /** The joined `data:` payload. */
  data: string;
}

export interface StreamOptions<T> {
  /** Path relative to the API base, e.g. `/client/projects/7/chat/stream`. */
  path: string;
  /** Cursor to resume from on the very first connect (from the chat fetch). */
  initialCursor?: string | null;
  /** Parsed JSON payload of each frame, already de-duplicated. */
  onEvent: (payload: T, frame: StreamFrame) => void;
  onPhaseChange?: (phase: StreamPhase, detail?: { attempt: number; retryInMs?: number }) => void;
  /** Called when the server refuses to resume and we must re-fetch state. */
  onResetRequested?: () => void;
  /** Called when a frame's data is not JSON. */
  onMalformedFrame?: (frame: StreamFrame) => void;
}

export interface StreamHandle {
  close: () => void;
  /** The last cursor successfully handed to `onEvent`. */
  lastCursor: () => string | null;
  /** Drop the stored cursor - used after a `stream_reset` + re-fetch. */
  resetCursor: (cursor: string | null) => void;
}

const MAX_SEEN_CURSORS = 4096;
const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;
const MAX_BUFFER_BYTES = 8 * 1024 * 1024;

/** A bounded FIFO set. Old cursors fall out; the server never replays that far back. */
class CursorRing {
  private readonly set = new Set<string>();
  private readonly order: string[] = [];

  has(cursor: string): boolean {
    return this.set.has(cursor);
  }

  add(cursor: string): void {
    if (this.set.has(cursor)) return;
    this.set.add(cursor);
    this.order.push(cursor);
    if (this.order.length > MAX_SEEN_CURSORS) {
      const evicted = this.order.shift();
      if (evicted !== undefined) this.set.delete(evicted);
    }
  }

  clear(): void {
    this.set.clear();
    this.order.length = 0;
  }
}

/**
 * Incremental SSE frame parser.
 *
 * Frames are separated by a blank line. Within a frame, `field: value` lines
 * accumulate; a leading colon is a comment. A single space after the colon is
 * stripped, and only one - SSE is precise about this and a trimmed payload
 * would corrupt a value that legitimately begins with whitespace.
 */
export function parseFrames(chunk: string): { frames: StreamFrame[]; rest: string } {
  const CR = String.fromCharCode(13);
  const LF = String.fromCharCode(10);
  const normalized = chunk.split(CR + LF).join(LF).split(CR).join(LF);
  const parts = normalized.split(LF + LF);
  const rest = parts.pop() ?? '';
  const frames: StreamFrame[] = [];

  for (const raw of parts) {
    if (!raw.trim()) continue;

    let id: string | null = null;
    let event = 'message';
    const dataLines: string[] = [];

    for (const line of raw.split(LF)) {
      if (line === '' || line.startsWith(':')) continue; // keep-alive comment
      const colon = line.indexOf(':');
      const field = colon === -1 ? line : line.slice(0, colon);
      let value = colon === -1 ? '' : line.slice(colon + 1);
      if (value.startsWith(' ')) value = value.slice(1);

      switch (field) {
        case 'id':
          id = value;
          break;
        case 'event':
          if (value) event = value;
          break;
        case 'data':
          dataLines.push(value);
          break;
        case 'retry':
          // Server-suggested reconnect delay. We keep our own backoff policy -
          // it has jitter, which a fixed retry does not, and a hundred tabs
          // reconnecting on the same 3000ms tick is a thundering herd.
          break;
        default:
          break; // unknown fields are ignored, per spec
      }
    }

    if (dataLines.length === 0 && id === null) continue;
    frames.push({ id, event, data: dataLines.join(LF) });
  }

  return { frames, rest };
}

export function openStream<T>(options: StreamOptions<T>): StreamHandle {
  const { path, onEvent, onPhaseChange, onResetRequested, onMalformedFrame } = options;

  let closed = false;
  let cursor: string | null = options.initialCursor ?? null;
  let attempt = 0;
  let controller: AbortController | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  const seen = new CursorRing();

  const phase = (p: StreamPhase, detail?: { attempt: number; retryInMs?: number }) => {
    if (!closed || p === 'closed') onPhaseChange?.(p, detail);
  };

  /**
   * Full jitter: each client picks a random point in the window, so a mass
   * reconnect after a deploy spreads out instead of arriving as one spike.
   */
  const backoffMs = (n: number): number => {
    const ceiling = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** Math.min(n, 5));
    return Math.round(ceiling * (0.35 + Math.random() * 0.65));
  };

  const scheduleReconnect = () => {
    if (closed) return;
    const wait = backoffMs(attempt);
    attempt += 1;
    phase('reconnecting', { attempt, retryInMs: wait });
    retryTimer = setTimeout(() => {
      void connect();
    }, wait);
  };

  const handleFrame = (frame: StreamFrame) => {
    // A frame with an id we have already processed is a replay overlap. Record
    // the cursor anyway so a resume from here is still correct, but do not
    // deliver the payload twice.
    if (frame.id !== null) {
      if (seen.has(frame.id)) {
        cursor = frame.id;
        return;
      }
      seen.add(frame.id);
    }

    if (!frame.data) {
      if (frame.id !== null) cursor = frame.id;
      return;
    }

    let payload: T;
    try {
      payload = JSON.parse(frame.data) as T;
    } catch {
      onMalformedFrame?.(frame);
      if (frame.id !== null) cursor = frame.id;
      return;
    }

    const kind = (payload as { kind?: unknown })?.kind;

    if (kind === 'stream_reset') {
      // The server could not replay from our cursor. Adopt the cursor it hands
      // back, drop everything we think we know, and let the caller re-fetch.
      const from = (payload as { from_cursor?: string }).from_cursor ?? frame.id;
      seen.clear();
      cursor = from ?? null;
      onEvent(payload, frame);
      onResetRequested?.();
      return;
    }

    onEvent(payload, frame);

    // Advance only after the handler returned. A cursor moved before the event
    // is applied is a cursor that skips the event if the tab dies in between.
    if (frame.id !== null) cursor = frame.id;
  };

  const connect = async (): Promise<void> => {
    if (closed) return;
    retryTimer = null;
    controller = new AbortController();
    const signal = controller.signal;
    phase(attempt === 0 ? 'connecting' : 'reconnecting', { attempt });

    const runOnce = async (token: string | null): Promise<Response> => {
      const headers: Record<string, string> = { Accept: 'text/event-stream' };
      if (token) headers.Authorization = `Bearer ${token}`;
      if (cursor) headers['Last-Event-ID'] = cursor;

      const url = new URL(`${API_BASE}${path}`, window.location.origin);
      // Documented fallback for transports that strip the header. The server
      // prefers the header when both are present.
      if (cursor) url.searchParams.set('last_event_id', cursor);

      return fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers,
        signal,
        cache: 'no-store',
      });
    };

    try {
      let res = await runOnce(useAuthStore.getState().accessToken);

      if (res.status === 401) {
        const fresh = await refreshSession();
        if (!fresh) {
          useAuthStore.getState().clear();
          phase('failed', { attempt });
          return;
        }
        res = await runOnce(fresh);
      }

      if (res.status === 404 || res.status === 403) {
        // The project is gone or was unassigned. Reconnecting cannot fix it.
        phase('failed', { attempt });
        return;
      }

      if (!res.ok || !res.body) {
        scheduleReconnect();
        return;
      }

      attempt = 0;
      phase('open', { attempt });

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        // Guard against a server that never emits a frame separator: an
        // unbounded buffer is a memory leak that presents as a slow tab.
        if (buffer.length > MAX_BUFFER_BYTES) buffer = '';

        const { frames, rest } = parseFrames(buffer);
        buffer = rest;
        for (const frame of frames) handleFrame(frame);
      }

      // A clean end of body mid-run is expected - the server closes with a
      // grace period on shutdown. Reconnect from the cursor rather than
      // telling a client their work stopped.
      if (!closed) scheduleReconnect();
    } catch (err) {
      if (closed || (err instanceof DOMException && err.name === 'AbortError')) return;
      scheduleReconnect();
    }
  };

  void connect();

  return {
    close: () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      controller?.abort();
      phase('closed');
    },
    lastCursor: () => cursor,
    resetCursor: (next) => {
      cursor = next;
      seen.clear();
    },
  };
}
